import temml from 'temml';

import { logger } from '@zep/core-logger';

import { escapeHtml } from './escapeHtml';
import { normalizeMathliveLatex } from './normalizeMathliveLatex';

// 동일 LaTeX 입력의 반복 기록을 막는 모듈 단위 dedup. 에디터는 입력할 때마다
// 다시 렌더하므로 같은 입력이 여러 번 들어온다.
const reportedLatex = new Set<string>();

// 저장된 수식에 ESC 같은 제어 문자가 섞여 있다(`\u001b\pi = \frac{22}{7}`).
// Temml 이 거부하므로 공백으로 바꾼다. 탭·줄바꿈은 TeX 에서 의미가 있어(`%` 주석 종료) 둔다.
const CONTROL_CHARACTERS = /\p{Cc}/gu;
const TEX_WHITESPACE = new Set(['\t', '\n', '\r']);
const stripControlCharacters = (latex: string): string =>
  latex.replace(CONTROL_CHARACTERS, char =>
    TEX_WHITESPACE.has(char) ? char : ' ',
  );

/**
 * 전처리(제어 문자·MathLive 매크로) 후 Temml 로 MathML 을 만든다. 파싱에 실패하면 throw 한다.
 * 화면 렌더와 글자 수 세기(`countFormulaSymbols`)가 같은 결과를 보도록 둘 다 이걸 쓴다.
 */
export const parseLatexToMathML = (
  latex: string,
  { displayMode, annotate }: { displayMode: boolean; annotate: boolean },
): string =>
  temml.renderToString(normalizeMathliveLatex(stripControlCharacters(latex)), {
    displayMode,
    throwOnError: true,
    annotate,
  });

/**
 * Render a LaTeX string to MathML via Temml.
 *
 * `throwOnError: true`로 두어 파싱 실패 시 catch에서 폴백 문자열(`$...$` /
 * `$$...$$`)로 회복하며, 원본 LaTeX을 debug 로그로 1회 남긴다. 실패 원인은 작성자가
 * 쓴 잘못된 수식(입력 중인 에디터 미리보기 포함)이라 Sentry 알람 대상이 아니다. 폴백을
 * string으로 반환하므로 throw가 React render 트리로 전파되지 않아 global
 * error boundary는 작동하지 않는다.
 *
 * 반환값은 호출처에서 `dangerouslySetInnerHTML`로 주입되므로, 폴백 경로의
 * raw LaTeX은 escape하여 `<script>` 같은 페이로드가 DOM으로 새지 않도록 한다.
 */
export const renderLatexToMathML = (
  latex: string,
  displayMode = false,
): string => {
  try {
    return parseLatexToMathML(latex, { displayMode, annotate: true });
  } catch (e) {
    if (!reportedLatex.has(latex)) {
      reportedLatex.add(latex);
      logger.debug('latex_render_failed', {
        latex,
        displayMode,
        errorName: e instanceof Error ? e.name : 'unknown',
        errorMessage: e instanceof Error ? e.message : String(e),
      });
    }
    const safe = escapeHtml(latex);
    return displayMode ? `$$${safe}$$` : `$${safe}$`;
  }
};
